import {
  FormControlLabel,
  FormHelperText,
  IconButton,
  ButtonGroup,
  Switch,
  Input,
  makeStyles,
  Grid,
  TextField,
} from '@material-ui/core'
import {
  AddBox as AddIcon,
  IndeterminateCheckBox as MinusIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
} from '@material-ui/icons'
import { pickBy, uniq } from 'lodash'
import { Formik, FastField, useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo, useState } from 'react'
import equal from 'fast-deep-equal'

import { Button, Container, ProductList } from 'src/modules/ui'
import { useActionRunner } from 'src/modules/notification'

import { usePurchasing } from '../hooks'
import TotalBar from './TotalBar'
import RepeatOrderModal from './RepeatOrderModal'

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: 260,
    [theme.breakpoints.up('mobile')]: {
      marginBottom: 90,
    },
  },
  amountCell: {
    backgroundColor: 'white',
    borderRadius: theme.shape.borderRadius / 2,
    width: 50,
  },
  amountCellError: {
    color: theme.palette.error.main,
  },
  amountInput: {
    border: `1px solid ${theme.palette.fadedGreen.main}`,
    textAlign: 'center',
  },
  amountInputFocused: {
    borderColor: theme.palette.lightGreen.light,
  },
  favouriteIcon: {
    color: theme.palette.lightGreen.main,
  },
  favouriteFilter: {
    marginBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
    [theme.breakpoints.up('mobile')]: {
      marginBottom: theme.spacing(0),
    },
  },
  inputCell: {
    marginLeft: theme.spacing(2),
  },
  inputCellInner: {
    display: 'flex',
  },
  inputCellControl: {
    color: theme.palette.lightGreen.main,
    [theme.breakpoints.up('mobile')]: {
      display: 'none',
    },
  },
  productTable: {
    backgroundColor: theme.palette.fadedGreen.main,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    width: '100%',
  },
  totalBarInputs: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    [theme.breakpoints.up('mobile')]: {
      flexDirection: 'row',
    },
    [theme.breakpoints.up('tablet')]: {
      gap: '0px',
    },
  },
  withQuantitiesLabel: {
    color: 'white',
  },
  repeatOrderButton: {
    [theme.breakpoints.up('mobile')]: {
      marginRight: theme.spacing(1),
    },
  },
  draftButton: {
    width: '100%',
    [theme.breakpoints.up('mobile')]: {
      width: 'auto',
    },
  },
}))

const CreateOrder = ({
  initialQuantities,
  onSubmit,
  products,
  submitText,
  modalSupportProps,
  isModalOpen,
  handleOpenModal,
  handleCloseModal,
  handleSaveRepeatOrder,
  repeatOrder,
  onSetScheduleLater,
  scheduleLaterValue,
}) => {
  const classes = useStyles()
  const [showFavourites, setShowFavourites] = useState(false)
  const [filterByQuantity, setFilterByQuantity] = useState(false)
  const handleClickFilterByQuantity = (event, checked) =>
    setFilterByQuantity(checked)

  const productFilter = product =>
    showFavourites ? product.favourite : product

  const initialValues = products.reduce((values, product) => {
    values[String(product.id)] = initialQuantities[product.id] || ''
    return values
  }, {})

  return (
    <Container className={classes.root}>
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        {({ handleSubmit, isSubmitting, setFieldValue, values }) => (
          <FilteredProducts
            {...{
              filterByQuantity,
              handleSubmit,
              isSubmitting,
              onClickFilterByQuantity: handleClickFilterByQuantity,
              productFilter,
              showFavourites,
              setShowFavourites,
              products,
              submitText,
              setFieldValue,
              values,
              isModalOpen,
              handleOpenModal,
              handleCloseModal,
              handleSaveRepeatOrder,
              repeatOrder,
              onSetScheduleLater,
              scheduleLaterValue,
            }}
          />
        )}
      </Formik>
    </Container>
  )
}

const FilteredProducts = ({ products, values, ...props }) => {
  const { filterByQuantity } = props

  const [focused, setFocused] = useState()
  const handleFocus = useCallback(
    event => {
      const { target } = event
      setFocused(target.name)
    },
    [setFocused]
  )
  const handleBlur = useCallback(
    event => {
      setFocused()
    },
    [setFocused]
  )

  const filteredProducts = useMemo(() => {
    if (filterByQuantity) {
      return products.filter(prod => {
        const quantity = values[String(prod.id)]
        return quantity > 0 || focused === String(prod.id)
      })
    }
    return products
  }, [filterByQuantity, products, values, focused])

  return (
    <InnerForm
      {...props}
      products={filteredProducts}
      onFieldFocus={handleFocus}
      onFieldBlur={handleBlur}
    />
  )
}

const ScheduleOrder = ({ style, onSetFieldValue, scheduleLaterValue }) => {
  const getTomorrowsDate = () => {
    const today = new Date()
    today.setDate(today.getDate() + 1)
    return today.toISOString().slice(0, 10)
  }

  return (
    <Grid item style={{ marginRight: '2rem' }}>
      <form>
        {/* Date and Time Inputs */}
        <TextField
          id="date"
          label="Request Delivery Date"
          type="date"
          defaultValue={scheduleLaterValue ? scheduleLaterValue : ''}
          InputLabelProps={{
            shrink: true,
            style: { color: 'white' },
          }}
          InputProps={{
            style: { color: 'white', borderColor: 'white' },
          }}
          variant="outlined"
          onChange={onSetFieldValue}
          value={scheduleLaterValue}
          min={getTomorrowsDate()}
          style={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'white',
              },
              '&:hover fieldset': {
                borderColor: 'white',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'white',
              },
            },
          }}
        />
      </form>
    </Grid>
  )
}

const InnerForm = React.memo(
  ({
    filterByQuantity,
    filteredProducts,
    handleSubmit,
    isSubmitting,
    onClickFilterByQuantity,
    onFieldFocus,
    onFieldBlur,
    productFilter,
    showFavourites,
    setShowFavourites,
    products,
    submitText,
    setFieldValue,
    isModalOpen,
    handleOpenModal,
    handleCloseModal,
    handleSaveRepeatOrder,
    repeatOrder,
    onSetScheduleLater,
    scheduleLaterValue,
  }) => {
    const classes = useStyles()

    const MemoInputCell = useMemo(
      () => props => {
        return (
          <InputCell
            {...props}
            onBlur={onFieldBlur}
            onFocus={onFieldFocus}
            setFieldValue={setFieldValue}
          />
        )
      },
      [setFieldValue, onFieldBlur, onFieldFocus]
    )

    return (
      <>
        <ProductList
          productFilter={productFilter}
          productPreCell={FavouriteCell}
          productPostCell={MemoInputCell}
          products={products}
          showDescription
          showPhoto={false}
          showFilter
          showSearch
          showSupplierName
          tableClassName={classes.productTable}
          toolbarPostFilterComponent={() => (
            <FilterComponent
              className={classes.favouriteFilter}
              showFavourites={showFavourites}
              onSetShowFavourites={setShowFavourites}
            />
          )}
        />

        {/* <ScheduleOrder
          style={{ marginTop: '2rem' }}
          onSetFieldValue={onSetScheduleLater}
          scheduleLaterValue={scheduleLaterValue}
        /> */}
        <OrderTotal
          filterByQuantity={filterByQuantity}
          isSubmitting={isSubmitting}
          onClickFilterByQuantity={onClickFilterByQuantity}
          onSubmit={handleSubmit}
          products={products}
          submitText={submitText}
          isModalOpen={isModalOpen}
          handleOpenModal={handleOpenModal}
          handleCloseModal={handleCloseModal}
          handleSaveRepeatOrder={handleSaveRepeatOrder}
          repeatOrder={repeatOrder}
          onSetScheduleLater={onSetScheduleLater}
          scheduleLaterValue={scheduleLaterValue}
        />
      </>
    )
  },
  equal
)

const FilterComponent = ({
  className,
  showFavourites,
  onSetShowFavourites,
}) => {
  return (
    <ButtonGroup className={className}>
      <Button
        color={showFavourites ? 'default' : 'lightGreen'}
        onClick={() => onSetShowFavourites(false)}
      >
        All
      </Button>
      <Button
        color={showFavourites ? 'lightGreen' : 'default'}
        onClick={() => onSetShowFavourites(true)}
      >
        Favourites
      </Button>
    </ButtonGroup>
  )
}

const FavouriteCell = ({ product }) => {
  const classes = useStyles()
  const { favouriteProduct, unfavouriteProduct, purchasingEnvironment } =
    usePurchasing()
  const [doFavourite, isFavouriting] = useActionRunner(favouriteProduct)
  const [doUnfavourite, isUnfavouriting] = useActionRunner(unfavouriteProduct)

  if (product.favourite) {
    return (
      <IconButton
        size="small"
        disabled={isFavouriting}
        onClick={() =>
          doUnfavourite({
            id: product.id,
            environmentId: purchasingEnvironment,
          })
        }
        tabIndex={-1}
      >
        <FavoriteIcon className={classes.favouriteIcon} fontSize="small" />
      </IconButton>
    )
  } else {
    return (
      <IconButton
        size="small"
        disabled={isUnfavouriting}
        onClick={() =>
          doFavourite({ id: product.id, environmentId: purchasingEnvironment })
        }
        tabIndex={-1}
      >
        <FavoriteBorderIcon
          className={classes.favouriteIcon}
          fontSize="small"
        />
      </IconButton>
    )
  }
}

const InputCell = ({ onBlur, onFocus, product, setFieldValue }) => {
  const name = String(product.id)
  return (
    <FastField name={name}>
      {({ field, form, meta }) => (
        <InputCellInner
          field={field}
          meta={meta}
          onBlur={onBlur}
          onFocus={onFocus}
          setFieldValue={setFieldValue}
        />
      )}
    </FastField>
  )
}

const InputCellInner = ({ field, meta, onBlur, onFocus, setFieldValue }) => {
  const classes = useStyles()

  const setValue = useCallback(
    value => setFieldValue(field.name, value),
    [field.name, setFieldValue]
  )

  const { value } = field
  const { error } = meta

  const currentValue = Number(value || 0)
  const handleAdd = useCallback(() => {
    const newValue = currentValue + 1
    setValue(newValue)
  }, [currentValue, setValue])

  const handleSubtract = useCallback(() => {
    const newValue = currentValue - 1
    if (newValue > 0) {
      setValue(newValue)
    } else if (newValue === 0) {
      setValue('')
    }
  }, [currentValue, setValue])
  const handleBlur = useCallback(
    event => {
      field.onBlur(event)
      onBlur(event)
    },
    [field.onBlur, onBlur]
  )

  return (
    <div className={classes.inputCell}>
      <div className={classes.inputCellInner}>
        <IconButton
          className={classes.inputCellControl}
          size="small"
          onClick={handleSubtract}
        >
          <MinusIcon />
        </IconButton>
        <Input
          autoComplete="off"
          classes={{
            root: classes.amountCell,
            focused: classes.amountInputFocused,
            input: classes.amountInput,
            error: classes.amountCellError,
          }}
          disableUnderline
          error={!!error}
          placeholder="0"
          onFocus={onFocus}
          {...field}
          onBlur={handleBlur}
        />
        <IconButton
          className={classes.inputCellControl}
          size="small"
          onClick={handleAdd}
        >
          <AddIcon />
        </IconButton>
      </div>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </div>
  )
}

const OrderTotal = ({
  filterByQuantity,
  isSubmitting,
  onSubmit,
  onClickFilterByQuantity,
  products,
  submitText,
  isModalOpen,
  handleOpenModal,
  handleCloseModal,
  handleSaveRepeatOrder,
  repeatOrder,
  onSetScheduleLater,
  scheduleLaterValue,
}) => {
  const { values } = useFormikContext()
  const classes = useStyles()
  const selectedValues = pickBy(values)
  const numProducts = Object.values(selectedValues).length
  const selectedProducts = products.filter(p => selectedValues[p.id])
  const total = selectedProducts.reduce((total, product) => {
    const pricePerUnit = product.variablePrice
      ? product.price * product.avEquivalentUnitSize
      : product.price
    const subtotal =
      Number(pricePerUnit).toFixed(2) * selectedValues[product.id]
    return total + subtotal
  }, 0)

  const numSuppliers = uniq(selectedProducts.map(p => p.supplier.id)).length

  return (
    <TotalBar
      numProducts={numProducts}
      numSuppliers={numSuppliers}
      total={total}
    >
      {isModalOpen && (
        <RepeatOrderModal
          open={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveRepeatOrder}
          values={values}
          repeatOrder={repeatOrder}
        />
      )}
      <ScheduleOrder
        onSetFieldValue={onSetScheduleLater}
        scheduleLaterValue={scheduleLaterValue}
      />
      <div className={classes.totalBarInputs}>
        <FormControlLabel
          className={classes.withQuantitiesLabel}
          control={
            <Switch
              checked={filterByQuantity}
              onChange={onClickFilterByQuantity}
              color="secondary"
            />
          }
          label="With Quantities"
        />

        <Button
          disabled={numProducts === 0}
          onClick={handleOpenModal}
          color="orange"
          size="large"
          className={classes.repeatOrderButton}
        >
          {repeatOrder ? 'Edit Repeating Order' : 'Schedule Repeating Order'}
        </Button>

        <Button
          disabled={numProducts === 0 || isSubmitting}
          onClick={onSubmit}
          color="orange"
          isLoading={isSubmitting}
          size="large"
          className={classes.draftButton}
        >
          {submitText}
        </Button>
      </div>
    </TotalBar>
  )
}

CreateOrder.propTypes = {
  initialQuantities: PropTypes.object,
  onSubmit: PropTypes.func,
  products: PropTypes.array,
  submitText: PropTypes.string,
  isModalOpen: PropTypes.bool,
  handleOpenModal: PropTypes.func,
  handleCloseModal: PropTypes.func,
  handleSaveRepeatOrder: PropTypes.func,
  repeatOrderSchedule: PropTypes.object,
}

CreateOrder.defaultProps = {
  initialQuantities: {},
  submitText: 'Submit',
}

export default CreateOrder
